<template>
  <div v-if="course" class="w-full h-full">
    <!-- header -->
    <div class="flex item-center justify-between p-[12px] bg-gray-100 sticky left-0 top-0 z-[1000]">
      <div class="flex items-center gap-3">
        <ChevronLeftIcon class="size-[24px]" @click="goBack" />
        <div class="text-xl font-medium nowrap">
          Курс: {{ course.title }}
        </div>
      </div>

      <div class="flex items-center gap-3">
        <button
            class="h-[40px] px-3 w-max rounded-[6px] border border-gray-400 text-gray-600 cursor-pointer"
            @click="onCopyCourse"
        >
          Скопировать
        </button>
        <button
            class="h-[40px] px-3 w-max rounded-[6px] bg-[#574AEA] text-white cursor-pointer"
            @click="onSaveCourse"
            :disabled="isSaving"
        >
          {{ isSaving ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </div>
    </div>
    <!-- ./header -->

    <div class="flex">
      <!-- structure -->
      <div class="w-[300px] bg-gray-100">
        <div
          v-for="(item, index) of structure"
          :key="index"
        >
          <template v-if="item.type === 'settings'">
            <div
              class="px-[12px] py-[16px] border-b border-gray-200 hover:bg-[#e7f9fe] cursor-pointer"
              :class="{ 'bg-[#e7f9fe] border-l-[4px] border-[#48cef8] border-b-0': contentType === item.type }"
              @click="item.action"
            >
              {{ item.title }}
            </div>
          </template>

          <template v-if="item.type === 'description'">
            <div
              class="px-[12px] py-[16px] border-b border-gray-200 hover:bg-[#e7f9fe] cursor-pointer"
             :class="{ 'bg-[#e7f9fe] border-l-[4px] border-[#48cef8] border-b-0': contentType === item.type }"
              @click="item.action"
            >
              {{ item.title }}
            </div>
          </template>
        </div>

        <!-- lessons -->
        <div class="py-4 border-b border-gray-200">
          <div class="mb-3 font-medium px-3 flex justify-between items-center">
            <span>Уроки</span>
            <button 
              v-if="isLessonOrderChanged && course.lessons && course.lessons.length > 1" 
              @click="saveLessonOrder"
              class="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              :disabled="isSavingOrder"
            >
              <span v-if="isSavingOrder">Сохранение...</span>
              <span v-else>Сохранить порядок</span>
            </button>
          </div>
          <div v-if="course.lessons && course.lessons.length > 0" class="mb-3">
            <div ref="lessonsList" class="lessons-list">
              <div
                v-for="(lesson, lessonIndex) in course.lessons"
                :key="lesson.id || lessonIndex"
                :data-id="lesson.id || 'new-' + lessonIndex"
                class="group py-4 px-3 cursor-move hover:bg-gray-100 flex items-center justify-between lesson-item"
                :class="{ 'bg-blue-50 border-l-4 border-primary': contentType === 'lesson' && currentLessonIndex === lessonIndex }"
              >
                <div class="flex items-center flex-1 pointer-events-none">
                  <div 
                    class="mr-2 text-gray-400" 
                    title="Перетащите для изменения порядка"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
                    </svg>
                  </div>
                  <div @click="showLesson(lessonIndex)" class="flex-1 cursor-pointer pointer-events-auto">
                    {{ lessonIndex + 1 }}. {{ lesson.title || 'Без названия' }}
                  </div>
                </div>
                <button
                  v-if="lesson.id"
                  @click="confirmDeleteLesson(lesson.id)"
                  class="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-all pointer-events-auto"
                  title="Удалить урок"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div class="w-full px-3">
            <button
                class="h-[40px] w-full px-3 rounded-[6px] bg-[#574AEA] text-white cursor-pointer hover:bg-[#473add]"
                @click="onAddLesson"
            >
              Добавить урок
            </button>
          </div>
        </div>
        <!-- ./lessons -->

        <!-- tests -->
        <div class="py-4 border-b border-gray-200">
          <div class="mb-3 font-medium px-3">Тесты</div>
          <div v-if="course.tests && course.tests.length > 0" class="mb-3">
            <div
              v-for="(test, testIndex) in course.tests"
              :key="testIndex"
              class="group py-4 px-3 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
              :class="{ 'bg-purple-50 border-l-4 border-purple-500': contentType === 'test' && currentTestIndex === testIndex }"
            >
              <div @click="showTest(testIndex)" class="flex-1">
                {{ testIndex + 1 }}. {{ test.title || 'Без названия' }}
              </div>
              <button
                v-if="test.id"
                @click="confirmDeleteTest(test.id)"
                class="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-all"
                title="Удалить тест"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="w-full px-3">
            <button
                class="h-[40px] w-full px-3 rounded-[6px] bg-purple-500 text-white cursor-pointer hover:bg-[#473add]"
                @click="onAddTest"
            >
              Создать тест
            </button>
          </div>
        </div>
        <!-- ./tests -->
      </div>
      <!-- ./structure -->

      <!-- content-chose-from-structure -->
      <div class="w-full px-[24px]">
        <SettingsCourse v-if="contentType === 'settings'" :course="course" @update:course="(updatedCourse) => { course = updatedCourse }" @course-deleted="onCourseDeleted" />

        <DescriptionFullCourse v-else-if="contentType === 'description'" :course="course" @update:course="(updatedCourse) => { course = updatedCourse }"  />

        <!-- Отображаем урок при выборе урока -->
        <LessonEditor v-else-if="contentType === 'lesson'"
          :key="'lesson-' + currentLessonIndex + '-' + (course.lessons[currentLessonIndex]?.id || 'new')"
          :lesson="course.lessons[currentLessonIndex]"
          @update:lesson="updateLesson"
          @deleteLesson="confirmDeleteLesson"
        />

        <!-- Отображаем тест при выборе теста -->
        <TestEditor v-else-if="contentType === 'test'"
          :test="course.tests[currentTestIndex]"
          :courseId="props.courseId"
          @update:test="updateTest"
          @deleteTest="executeDeleteTest"
        />

        <!-- Назначения курса -->
        <div v-else-if="contentType === 'assignments'" class="space-y-4">
          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <div class="text-md font-medium mb-3">Назначить курс сотрудникам</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm text-gray-600 mb-2">Сотрудники</label>
                <select v-model="assignForm.userIds" multiple size="10" class="w-full border rounded-md p-2 text-sm">
                  <option v-for="u in portalUsers" :key="u.ID" :value="String(u.ID)">
                    {{ (u.LAST_NAME || '') + ' ' + (u.NAME || '') }}
                  </option>
                </select>
                <p class="text-xs text-gray-500 mt-1">Выберите одного или нескольких сотрудников</p>
              </div>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm text-gray-600 mb-1">Дедлайн (необязательно)</label>
                  <input type="date" v-model="assignForm.dueAt" class="w-full border rounded-md p-2 text-sm" />
                </div>
                <div class="flex items-center gap-2">
                  <input id="cbx-b24" type="checkbox" v-model="assignForm.createB24Tasks" class="cursor-pointer" />
                  <label for="cbx-b24" class="text-sm text-gray-700 cursor-pointer">Создать задачу в Bitrix24</label>
                </div>
                <div>
                  <button @click="onAssignUsers" :disabled="isAssigning || assignForm.userIds.length === 0"
                          class="px-4 py-2 rounded-md bg-[#574AEA] text-white disabled:opacity-50">
                    {{ isAssigning ? 'Назначение...' : 'Назначить' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl border border-gray-200 p-4">
            <div class="text-md font-medium mb-3">Список назначений</div>
            <div v-if="assignments.length === 0" class="text-sm text-gray-500">Назначений пока нет</div>
            <div v-else class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="text-left text-gray-500">
                    <th class="py-2 pr-4">Сотрудник</th>
                    <th class="py-2 pr-4">Должность</th>
                    <th class="py-2 pr-4">Статус</th>
                    <th class="py-2 pr-4">Дедлайн</th>
                    <th class="py-2 pr-4">Назначено</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in assignments" :key="a.id" class="border-t">
                    <td class="py-2 pr-4">
                      <UserCard
                        :avatarUrl="userPhoto(a.userId)"
                        :title="userName(a.userId)"
                        :subtitle="userPosition(a.userId)"
                        size="md"
                        shape="rounded-xl"
                      />
                    </td>
                    <td class="py-2 pr-4">{{ userPosition(a.userId) }}</td>
                    <td class="py-2 pr-4">{{ mapStatus(a.status) }}</td>
                    <td class="py-2 pr-4">{{ a.dueAt ? formatDate(a.dueAt) : '' }}</td>
                    <td class="py-2 pr-4">{{ a.assignedAt ? formatDate(a.assignedAt) : '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <!-- ./content-chose-from-structure -->
    </div>
  </div>

  <!-- Модалки подтверждения удаления -->
  <div v-if="showDeleteTestConfirm || showDeleteLessonConfirm" class="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
    <div class="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200">
      <div class="text-center">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
          </svg>
        </div>
        <div class="text-lg font-semibold text-gray-900 mb-2">
          {{ showDeleteTestConfirm ? 'Удалить тест?' : 'Удалить урок?' }}
        </div>
        <p class="text-gray-600 mb-6">
          {{ showDeleteTestConfirm 
              ? 'Вы точно хотите удалить этот тест? Это действие нельзя отменить.' 
              : 'Вы точно хотите удалить этот урок? Это действие нельзя отменить.' }}
        </p>
        
        <div class="flex gap-3">
          <button
            @click="showDeleteTestConfirm ? cancelDeleteTest() : cancelDeleteLesson()"
            class="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Отмена
          </button>
          <button
            @click="showDeleteTestConfirm ? executeDeleteTest() : executeDeleteLesson()"
            class="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import apiService from '@/services/api'
import ChevronLeftIcon from '@/components/ui/icons/chevron-left-icon.vue'
import SettingsCourse from '@/components/course/SettingsCourse.vue'
import DescriptionFullCourse from '@/components/course/DescriptionFullCourse.vue'
import LessonEditor from '@/components/course/LessonEditor.vue'
import TestEditor from '@/components/course/TestEditor.vue'
import { useToast } from "vue-toastification";
import { usePortalStore } from '@/stores/portalStore'
import Sortable from 'sortablejs'
import { computed } from 'vue'
import UserCard from '@/components/ui/UserCard.vue'

import {defineEmits, defineProps, onMounted, onUnmounted, ref, nextTick} from 'vue'

const emit = defineEmits(['onCloseCourseModal'])

const props = defineProps({
  courseId: {
    type: String,
    required: true
  }
})

const toast = useToast();
const portalStore = usePortalStore()

const structure = ref({})
const course = ref(null)
const contentType = ref('settings')
const currentLessonIndex = ref(-1)
const currentTestIndex = ref(-1)
const isSaving = ref(false)

// Состояние для модалок подтверждения
const isLessonOrderChanged = ref(false)
const isSavingOrder = ref(false)
const showDeleteTestConfirm = ref(false)
const showDeleteLessonConfirm = ref(false)
const deleteTestId = ref(null)
const deleteLessonId = ref(null)

// Назначения
const assignments = ref([])
const isAssigning = ref(false)
const assignForm = ref({ userIds: [], dueAt: '', createB24Tasks: true })

const portalUsers = computed(() => (portalStore.portalData.allUsers || []).slice().sort((a, b) => {
  const an = `${a.LAST_NAME || ''} ${a.NAME || ''}`.trim().toLowerCase()
  const bn = `${b.LAST_NAME || ''} ${b.NAME || ''}`.trim().toLowerCase()
  return an.localeCompare(bn)
}))

const userName = (id) => {
  const u = (portalStore.portalData.allUsers || []).find(x => String(x.ID) === String(id))
  return u ? `${u.LAST_NAME || ''} ${u.NAME || ''}`.trim() || String(id) : String(id)
}

const userPosition = (id) => {
  const u = (portalStore.portalData.allUsers || []).find(x => String(x.ID) === String(id))
  return u?.WORK_POSITION || ''
}

const userPhoto = (id) => {
  const u = (portalStore.portalData.allUsers || []).find(x => String(x.ID) === String(id))
  return u?.PERSONAL_PHOTO || 'https://cdn-icons-png.flaticon.com/128/3177/3177440.png'
}

const formatDate = (d) => {
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}

const mapStatus = (s) => ({ assigned: 'Назначен', completed: 'Сдан', expired: 'Просрочен' }[s] || s)

const loadAssignments = async () => {
  try {
    const res = await apiService.listAssignments(props.courseId)
    assignments.value = res?.data || []
  } catch (e) { console.error(e) }
}

const onAssignUsers = async () => {
  if (assignForm.value.userIds.length === 0) return
  try {
    isAssigning.value = true
    const payload = {
      userIds: assignForm.value.userIds,
      createB24Tasks: !!assignForm.value.createB24Tasks,
      ...(assignForm.value.dueAt ? { dueAt: new Date(assignForm.value.dueAt).toISOString() } : {}),
    }
    await apiService.assignCourse(props.courseId, payload)
    toast.success('Курс назначен')
    assignForm.value = { userIds: [], dueAt: '', createB24Tasks: true }
    await loadAssignments()
  } catch (e) {
    console.error(e)
    toast.error('Не удалось назначить курс')
  } finally {
    isAssigning.value = false
  }
}

const onCopyCourse = async () => {
  const urlApp = `https://${portalStore.portalData.portal.domain}/marketplace/app/${portalStore.portalData.portal.appId}?course=${course.value.id}`
  console.log('urlApp', urlApp)

  try {
    // Современный способ (работает в HTTPS и localhost)
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(urlApp)
      toast.success('Ссылка на курс скопирована')
      return
    }

    // Fallback для старых браузеров или HTTP
    const textArea = document.createElement('textarea')
    textArea.value = urlApp
    textArea.style.position = 'fixed'
    textArea.style.left = '-9999px'
    textArea.style.top = '-9999px'
    textArea.style.opacity = '0'
    textArea.style.pointerEvents = 'none'
    textArea.setAttribute('readonly', '')
    textArea.setAttribute('tabindex', '-1')

    document.body.appendChild(textArea)
    textArea.select()
    textArea.setSelectionRange(0, urlApp.length)

    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)

    if (successful) {
      toast.success('Ссылка на курс скопирована')
    } else {
      throw new Error('execCommand failed')
    }

  } catch (error) {
    console.error('Ошибка копирования:', error)

    // Последний fallback - показываем URL для ручного копирования
    const message = `Не удалось скопировать автоматически. Ссылка: ${urlApp}`

    // Если есть возможность показать prompt
    if (window.prompt) {
      window.prompt('Скопируйте ссылку вручную:', urlApp)
    } else {
      // Альтернатива через alert
      alert(message)
      console.log('URL для копирования:', urlApp)
    }
  }
}

// Сохраняем исходные пользователи для отправки уведомлений только новым
const originalAccessibleUsers = ref([])

// Сохраняем исходный порядок уроков для отслеживания изменений
const originalLessonOrder = ref([])

const showLesson = (index) => {
  currentLessonIndex.value = index
  currentTestIndex.value = -1
  contentType.value = 'lesson'
}

const showTest = async (index) => {
  currentTestIndex.value = index
  currentLessonIndex.value = -1
  contentType.value = 'test'
  
  // Загружаем полные данные теста с вопросами
  if (course.value?.tests[index]?.id) {
    try {
      const fullTest = await apiService.getTest(props.courseId, course.value.tests[index].id)
      // Обновляем данные теста в локальной копии курса
      course.value.tests[index] = fullTest
    } catch (error) {
      console.error('Ошибка при загрузке полного теста:', error)
      toast.error('Ошибка при загрузке данных теста.')
    }
  }
}

const updateLesson = (updatedLesson) => {
  if (currentLessonIndex.value >= 0 && course.value.lessons) {
    course.value.lessons[currentLessonIndex.value] = updatedLesson
  }
}

const updateTest = (updatedTest) => {
  if (currentTestIndex.value >= 0 && course.value.tests) {
    course.value.tests[currentTestIndex.value] = updatedTest
  }
}

const onAddLesson = () => {
  if (!course.value.lessons) {
    course.value.lessons = []
  }
  
  course.value.lessons.push({
    title: 'Новый урок',
    content: ''
  })
  
  // Сразу показываем созданный урок
  showLesson(course.value.lessons.length - 1)
}

const onAddTest = () => {
  if (!course.value.tests) {
    course.value.tests = []
  }
  
  course.value.tests.push({
    title: 'Новый тест',
    timeLimit: 30,
    attemptsAllowed: 3,
    requiredCorrectAnswers: 1,
    questions: []
  })
  
  // Сразу показываем созданный тест
  showTest(course.value.tests.length - 1)
}

const onSaveCourse = async () => {
  if (isSaving.value) return

  try {
    isSaving.value = true

    // Обновляем общую информацию о курсе
    await apiService.updateCourse(props.courseId, {
      title: course.value.title,
      description: course.value.description,
      extendedDescription: course.value.extendedDescription,
      isActive: course.value.isActive,
      accessibleUsers: course.value.accessibleUsers
    })

    // Обрабатываем уроки
    if (course.value.lessons && course.value.lessons.length > 0) {
      for (const [index, lesson] of course.value.lessons.entries()) {
        if (lesson.id) {
          await apiService.updateLesson(props.courseId, lesson.id, {
            title: lesson.title,
            content: lesson.content,
            order: index
          })
        } else {
          const response = await apiService.createLesson(props.courseId, {
            title: lesson.title,
            content: lesson.content,
            order: index
          })

          if (response && response.data) {
            lesson.id = response.data.id
          }
        }
      }
    }

    // Обрабатываем тесты
    if (course.value.tests && course.value.tests.length > 0) {
      for (const test of course.value.tests) {
        let testId = test.id

        // Готовим данные для отправки с учетом галочки
        const testPayload = {
          title: test.title,
          timeLimit: test.timeLimit,
          attemptsAllowed: test.attemptsAllowed,
          requiredCorrectAnswers: test.requiredCorrectAnswers || 1,
          // Если галочка стоит - отправляем число, если нет - null
          numberOfQuestionsToDisplay: test.useRandomQuestions ? test.numberOfQuestionsToDisplay : null,
        }

        if (test.id) {
          // Обновляем существующий тест
          await apiService.updateTest(props.courseId, test.id, testPayload)
        } else {
          // Создаем новый тест
          const response = await apiService.createTest(props.courseId, testPayload)

          if (response && response.data) {
            test.id = response.data.id
            testId = response.data.id
          }
        }

        // Обрабатываем вопросы теста
        if (test.questions && test.questions.length > 0 && testId) {
          for (const question of test.questions) {
            const questionPayload = {
              question: question.question,
              options: question.options,
              correctAnswers: question.correctAnswers,
              isCritical: question.isCritical,
              isSingleAnswer: question.isSingleAnswer,
            }

            if (question.id) {
              // Обновляем существующий вопрос
              await apiService.updateQuestion(props.courseId, testId, question.id, questionPayload)
            } else {
              // Создаем новый вопрос
              const response = await apiService.createQuestion(props.courseId, testId, questionPayload)

              if (response && response.data) {
                question.id = response.data.id
              }
            }
          }
        }
      }
    }

    // Обновляем список исходных пользователей
    originalAccessibleUsers.value = [...course.value.accessibleUsers]

    toast.success('Курс успешно сохранен')
  } catch (error) {
    console.error('Ошибка при сохранении курса:', error)
    toast.error('Ошибка при сохранении курса')
  } finally {
    isSaving.value = false
  }
}

// Функции для управления скроллом документа
const hideBodyScroll = () => {
  document.body.style.overflow = 'hidden'
}

const showBodyScroll = () => {
  document.body.style.overflow = ''
}

const goBack = () => {
  showBodyScroll()
  emit('onCloseCourseModal')
}

// Функции для подтверждения удаления тестов
const confirmDeleteTest = (testId) => {
  deleteTestId.value = testId
  showDeleteTestConfirm.value = true
}

const cancelDeleteTest = () => {
  deleteTestId.value = null
  showDeleteTestConfirm.value = false
}

const executeDeleteTest = async () => {
  if (deleteTestId.value) {
    try {
      await apiService.deleteTest(props.courseId, deleteTestId.value)
      
      // Удаляем тест из локального состояния
      course.value.tests = course.value.tests.filter(test => test.id !== deleteTestId.value)
      
      // Если удалили текущий редактируемый тест, переключаемся на настройки курса
      if (course.value.tests.length === 0 || (contentType.value === 'test' && course.value.tests[currentTestIndex.value]?.id !== deleteTestId.value)) {
         contentType.value = 'settings'
         currentTestIndex.value = -1
      }
      
      toast.success('Тест успешно удален')
    } catch (error) {
      console.error('Ошибка при удалении теста:', error)
      toast.error('Ошибка при удалении теста')
    } finally {
      cancelDeleteTest()
    }
  }
}

// Функции для подтверждения удаления уроков
const confirmDeleteLesson = (lessonId) => {
  deleteLessonId.value = lessonId
  showDeleteLessonConfirm.value = true
}

const cancelDeleteLesson = () => {
  deleteLessonId.value = null
  showDeleteLessonConfirm.value = false
}

const executeDeleteLesson = async () => {
  if (deleteLessonId.value) {
    try {
      await apiService.deleteLesson(props.courseId, deleteLessonId.value)
      
      // Удаляем урок из локального состояния
      course.value.lessons = course.value.lessons.filter(lesson => lesson.id !== deleteLessonId.value)
      
      // Если удалили текущий редактируемый урок, переключаемся на настройки курса
      if (course.value.lessons.length === 0 || (contentType.value === 'lesson' && course.value.lessons[currentLessonIndex.value]?.id !== deleteLessonId.value)) {
         contentType.value = 'settings'
         currentLessonIndex.value = -1
      }
      
      toast.success('Урок успешно удален')
    } catch (error) {
      console.error('Ошибка при удалении урока:', error)
      toast.error('Ошибка при удалении урока')
    } finally {
      cancelDeleteLesson()
    }
  }
}

const onCourseDeleted = () => {
  emit('onCloseCourseModal')
}

// Ссылка на экземпляр Sortable
let sortableInstance = null

// Инициализация Sortable
const initSortable = () => {
  nextTick(() => {
    const el = document.querySelector('.lessons-list')
    if (!el) return
    
    console.log('Initializing Sortable')
    
    // Уничтожаем предыдущий экземпляр, если он существует
    if (sortableInstance) {
      sortableInstance.destroy()
    }
    
    // Создаем новый экземпляр Sortable
    sortableInstance = Sortable.create(el, {
      animation: 150,
      draggable: '.lesson-item',
      disabled: isSavingOrder.value,
      onEnd: (evt) => {
        console.log('Sortable onEnd event:', evt)
        updateLessonOrder(evt.oldIndex, evt.newIndex)
      }
    })
  })
}

// Обновление порядка уроков после перетаскивания
const updateLessonOrder = (oldIndex, newIndex) => {
  console.log(`Moving lesson from index ${oldIndex} to ${newIndex}`)
  
  // Если индексы одинаковы, ничего не делаем
  if (oldIndex === newIndex) return
  
  // Сохраняем текущий выбранный урок
  const currentSelectedLessonId = contentType.value === 'lesson' && currentLessonIndex.value >= 0 
    ? course.value.lessons[currentLessonIndex.value]?.id 
    : null
  
  // Получаем перемещаемый элемент
  const movedLesson = course.value.lessons[oldIndex]
  
  // Создаем новый массив с обновленным порядком
  const updatedLessons = [...course.value.lessons]
  
  // Удаляем элемент из старой позиции
  updatedLessons.splice(oldIndex, 1)
  
  // Вставляем его в новую позицию
  updatedLessons.splice(newIndex, 0, movedLesson)
  
  // Обновляем порядок для всех уроков
  const reorderedLessons = updatedLessons.map((lesson, index) => {
    return { ...lesson, order: index }
  })
  
  // Обновляем список уроков
  course.value.lessons = reorderedLessons
  
  // Проверяем, изменился ли порядок по сравнению с исходным
  isLessonOrderChanged.value = !originalLessonOrder.value.every((id, index) => {
    return reorderedLessons[index]?.id === id
  })
  
  // Обновляем индекс текущего выбранного урока
  if (currentSelectedLessonId) {
    const newIndex = reorderedLessons.findIndex(lesson => lesson.id === currentSelectedLessonId)
    if (newIndex !== -1) {
      currentLessonIndex.value = newIndex
    }
  }
  
  console.log('Updated lessons order:', reorderedLessons.map(l => ({ id: l.id, title: l.title, order: l.order })))
  console.log('Current lesson index is now:', currentLessonIndex.value)
}

// Сохранение нового порядка уроков
const saveLessonOrder = async () => {
  if (!course.value?.lessons || course.value.lessons.length <= 1) return
  
  try {
    isSavingOrder.value = true
    
    // Подготовка данных для API
    const lessonsToUpdate = course.value.lessons.map((lesson, index) => {
      return { id: lesson.id, order: index }
    }).filter(lesson => lesson.id) // Отфильтровываем уроки без id
    
    // Вызов API для обновления порядка
    await apiService.reorderLessons(props.courseId, lessonsToUpdate)
    
    // Обновляем исходный порядок после успешного сохранения
    originalLessonOrder.value = course.value.lessons.map(lesson => lesson.id)
    isLessonOrderChanged.value = false
    
    toast.success('Порядок уроков успешно обновлен')
  } catch (error) {
    console.error('Ошибка при обновлении порядка уроков:', error)
    toast.error('Не удалось обновить порядок уроков')
  } finally {
    isSavingOrder.value = false
  }
}

onMounted(async () => {
  hideBodyScroll()
  
  const data = await apiService.getCourse(props.courseId)
  
  // Инициализируем пустые массивы, если их нет
  if (!data.lessons) data.lessons = []
  if (!data.tests) data.tests = []
  if (!data.accessibleUsers) data.accessibleUsers = []
  
  course.value = data
  
  // Сохраняем исходных пользователей для определения новых
  originalAccessibleUsers.value = [...data.accessibleUsers]
  
  // Сохраняем исходный порядок уроков
  originalLessonOrder.value = data.lessons.map(lesson => lesson.id)
  
  // Инициализируем Sortable для перетаскивания уроков
  initSortable()

  structure.value = [
    {
      type: 'settings',
      title: 'Настройки курса',
      action: () => {
        contentType.value = 'settings'
        currentLessonIndex.value = -1
        currentTestIndex.value = -1
      }
    },
    {
      type: 'description',
      title: 'Описание курса',
      action: () => {
        contentType.value = 'description'
        currentLessonIndex.value = -1
        currentTestIndex.value = -1
      }
    },
    {
      type: 'assignments',
      title: 'Назначения',
      action: () => {
        contentType.value = 'assignments'
        currentLessonIndex.value = -1
        currentTestIndex.value = -1
        loadAssignments()
      }
    },
  ]
})

onUnmounted(() => {
  showBodyScroll()
  
  // Уничтожаем экземпляр Sortable при размонтировании компонента
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
})
</script>

<style scoped>
/* Мобильная адаптивность */
@media (max-width: 768px) {
  /* Основной контейнер */
  .w-full.h-full {
    height: 100vh;
    width: 100vw;
  }
  
  /* Header адаптация */
  .flex.item-center.justify-between.p-\[12px\] {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .text-xl.font-medium.nowrap {
    font-size: 1rem;
    white-space: normal;
    line-height: 1.3;
  }
  
  .h-\[40px\].px-3.w-max.rounded-\[6px\] {
    height: 44px;
    padding: 0 16px;
    font-size: 0.875rem;
    border-radius: 8px;
    min-width: 120px;
  }
  
  .flex {
    flex-direction: column;
  }
  
  /* Sidebar navigation */
  .px-\[12px\].py-\[16px\] {
    padding: 12px 16px;
  }
  
  .group.py-4.px-3 {
    padding: 12px 16px;
  }
  
  /* Content area */
  .w-full.px-\[24px\] {
    width: 100%;
    padding: 16px;
  }
  
  /* Кнопки добавления */
  .h-\[40px\].w-full.px-3.rounded-\[6px\] {
    height: 48px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
  }
  
  .w-full.px-3 {
    padding: 0 16px;
  }
  
  /* Модальное окно подтверждения */
  .bg-white.rounded-2xl.p-8.max-w-md.mx-4 {
    max-width: calc(100vw - 32px);
    margin: 16px;
    padding: 16px;
    border-radius: 16px;
  }
  
  .w-16.h-16 {
    width: 48px;
    height: 48px;
  }
  
  .text-lg.font-semibold {
    font-size: 1rem;
  }
  
  .mb-6 {
    margin-bottom: 16px;
  }
  
  .mb-4 {
    margin-bottom: 12px;
  }
  
  .flex.gap-3 {
    gap: 12px;
  }
  
  .flex-1.py-2.px-4 {
    padding: 12px 16px;
    font-size: 0.875rem;
    min-height: 44px;
    border-radius: 8px;
  }
}

@media (max-width: 480px) {
  /* Дополнительная компактность для маленьких экранов */
  .flex.item-center.justify-between.p-\[12px\] {
    padding: 8px 12px;
  }
  
  .text-xl.font-medium.nowrap {
    font-size: 0.9rem;
  }
  
  .h-\[40px\].px-3.w-max.rounded-\[6px\] {
    height: 40px;
    padding: 0 12px;
    font-size: 0.8rem;
    min-width: 100px;
  }
  
  .px-\[12px\].py-\[16px\] {
    padding: 8px 12px;
  }
  
  .group.py-4.px-3 {
    padding: 8px 12px;
  }
  
  .w-full.px-\[24px\] {
    padding: 12px;
  }
  
  .h-\[40px\].w-full.px-3.rounded-\[6px\] {
    height: 44px;
    padding: 0 12px;
    font-size: 0.8rem;
  }
  
  .w-full.px-3 {
    padding: 0 12px;
  }
  
  .bg-white.rounded-2xl.p-8.max-w-md.mx-4 {
    max-width: calc(100vw - 16px);
    margin: 8px;
    padding: 12px;
  }
  
  .w-16.h-16 {
    width: 40px;
    height: 40px;
  }
  
  .text-lg.font-semibold {
    font-size: 0.9rem;
  }
  
  .flex-1.py-2.px-4 {
    padding: 10px 12px;
    font-size: 0.8rem;
    min-height: 40px;
  }
}
</style>
